import type { DeclaredType } from '../parser/ast.js';
import type { RuntimeValue, ScalarRuntimeValue } from './runtime-value.js';

/**
 * Ταξινομεί μια βαθμωτή τιμή στον αντίστοιχο τύπο ΓΛΩΣΣΑΣ/Ψευδογλώσσας —
 * ΑΚΕΡΑΙΕΣ ΜΟΝΟ για πραγματικά ακέραιους αριθμούς, ΠΡΑΓΜΑΤΙΚΕΣ για κάθε
 * άλλον αριθμό. Ίδιος διαχωρισμός με το `Interpreter.valueMatchesDeclaredType`
 * (τιμή, όχι στατικός τύπος) — βλ. `RuntimeArray.checkElementKind`.
 */
function classifyScalarType(value: ScalarRuntimeValue): DeclaredType {
  if (typeof value === 'boolean') return 'ΛΟΓΙΚΕΣ';
  if (typeof value === 'string') return 'ΧΑΡΑΚΤΗΡΕΣ';
  return Number.isInteger(value) ? 'ΑΚΕΡΑΙΕΣ' : 'ΠΡΑΓΜΑΤΙΚΕΣ';
}

/**
 * Πίνακας κατά την εκτέλεση. Ενιαία αναπαράσταση (Map με σύνθετο κλειδί)
 * και για τις δύο διαλέκτους:
 * - ΓΛΩΣΣΑ: δηλωμένα, σταθερά όρια (`dimensions` != null) — γνωστά από τη
 *   ΜΕΤΑΒΛΗΤΕΣ. Επιτρέπει έλεγχο εκτός ορίων.
 * - Ψευδογλώσσα: χωρίς δηλώσεις, `dimensions === null` — ο πίνακας
 *   δημιουργείται δυναμικά στην πρώτη ανάθεση, χωρίς όρια προς έλεγχο.
 *
 * Δείκτες 1-based (όπως στη ΓΛΩΣΣΑ/Ψευδογλώσσα, όχι 0-based όπως στη JS).
 */
export class RuntimeArray {
  readonly dimensions: readonly number[] | null;
  private readonly cells = new Map<string, RuntimeValue>();
  /**
   * Ο τύπος (βλ. `classifyScalarType`) της ΠΡΩΤΗΣ βαθμωτής τιμής που
   * γράφτηκε σε ΟΠΟΙΟΔΗΠΟΤΕ στοιχείο — "κλειδώνει" τον τύπο του πίνακα όταν
   * αυτός δεν έχει ρητή δήλωση (βλ.
   * `Interpreter.requireConsistentArrayElementType`): ένας πίνακας δέχεται
   * μόνο ΕΝΑ είδος δεδομένων σε όλη του τη διάρκεια ζωής, ακόμα κι όταν
   * δημιουργείται δυναμικά (π.χ. σε "τμήμα αλγορίθμου", χωρίς ΜΕΤΑΒΛΗΤΕΣ) —
   * ΚΑΙ διακρίνει ΑΚΕΡΑΙΕΣ από ΠΡΑΓΜΑΤΙΚΕΣ (δεν επιτρέπονται ανάμεικτα).
   */
  private lockedElementType: DeclaredType | null = null;

  constructor(dimensions: readonly number[] | null) {
    this.dimensions = dimensions;
  }

  /**
   * Ελέγχει (και, στην πρώτη κλήση, κλειδώνει) τον τύπο των στοιχείων — βλ.
   * σχόλιο στο `lockedElementType`. Επιστρέφει τον ΗΔΗ κλειδωμένο τύπο αν η
   * νέα τιμή ΔΕΝ ταιριάζει, αλλιώς `null` (ταιριάζει, ή είναι η πρώτη τιμή
   * που γράφεται — οπότε μόλις κλειδώθηκε). Μια ΠΡΑΓΜΑΤΙΚΕΣ-κλειδωμένη
   * παράταξη δέχεται και ακέραιες τιμές (ίδιος ασύμμετρος κανόνας με δηλωμένο
   * τύπο, βλ. `valueMatchesDeclaredType`) — μόνο η αντίστροφη κατεύθυνση
   * (ΑΚΕΡΑΙΕΣ-κλειδωμένη παράταξη δέχεται πραγματικό αριθμό) απορρίπτεται.
   */
  checkElementKind(value: ScalarRuntimeValue): DeclaredType | null {
    const kind = classifyScalarType(value);
    if (this.lockedElementType === null) {
      this.lockedElementType = kind;
      return null;
    }
    if (this.lockedElementType === kind) return null;
    if (this.lockedElementType === 'ΠΡΑΓΜΑΤΙΚΕΣ' && kind === 'ΑΚΕΡΑΙΕΣ') return null;
    return this.lockedElementType;
  }

  private key(indices: readonly number[]): string {
    return indices.join(',');
  }

  get(indices: readonly number[]): RuntimeValue | undefined {
    return this.cells.get(this.key(indices));
  }

  set(indices: readonly number[], value: RuntimeValue): void {
    this.cells.set(this.key(indices), value);
  }

  /** true αν οι δείκτες είναι έγκυροι — πάντα true για δυναμικό (Ψευδογλώσσα) πίνακα. */
  isWithinBounds(indices: readonly number[]): boolean {
    if (this.dimensions === null) return true;
    if (indices.length !== this.dimensions.length) return false;
    return indices.every((idx, i) => Number.isInteger(idx) && idx >= 1 && idx <= this.dimensions![i]!);
  }

  /**
   * Όλα τα (μέχρι τώρα γραμμένα) στοιχεία — κλειδί: δείκτες ενωμένοι με
   * κόμμα, π.χ. "2,5" για το Α[2,5]. Για εμφάνιση (π.χ. panel μεταβλητών σε
   * web UI), όχι για μεταβολή· ένας δυναμικός (Ψευδογλώσσα) πίνακας μπορεί
   * να έχει «τρύπες» αν δεν έχουν γραφτεί ακόμα όλοι οι ενδιάμεσοι δείκτες.
   */
  entries(): ReadonlyMap<string, RuntimeValue> {
    return this.cells;
  }
}
